'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { AnimatePresence, motion } from 'framer-motion';
import type { Block, FormRenderSchema, RenderPage } from '@/app/utils/get-form-schema';
import { useAuth } from '@/contexts/AuthContext';
import DSIcon from '@/components/design-system/Icon';
import { BlockView } from './blockRenderers';
import { resolveNextPageId, resolvePagePath, visibleBlocks } from './conditions';
import { WizardNavContext, type AnswerSummary, type WizardNav } from './WizardNavContext';

const INPUT_TYPES = new Set(['text', 'textarea', 'email', 'phone', 'number', 'currency', 'select', 'multiselect', 'radio', 'checkbox', 'date', 'file', 'address', 'yesno', 'consent']);

// Short-text validation formats — kept in sync with the CMS shared types
// (separate repo, so duplicated here). Patterns are regex source strings.
const TEXT_FORMATS: Record<string, { pattern: string; message: string }> = {
    email: { pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$', message: 'Enter a valid email address' },
    phone: { pattern: '^[+]?[()\\d\\s-]{7,}$', message: 'Enter a valid phone number' },
    ssn: { pattern: '^\\d{3}-?\\d{2}-?\\d{4}$', message: 'Enter a valid SSN (e.g. 123-45-6789)' },
    alpha: { pattern: '^[A-Za-z\\s]+$', message: 'Letters only' },
    numeric: { pattern: '^\\d+$', message: 'Numbers only' }
};
const isInput = (b: Block) => INPUT_TYPES.has(b.type);
const fieldKey = (b: Block) => (b.props.key as string) || b.id;

type Values = Record<string, unknown>;

/**
 * Parts of an address answer that make it usable, with the wording used in
 * error messages. `line2` and `country` are never demanded. Kept in sync with
 * the CMS's own address check (separate repo).
 */
const ADDRESS_REQUIRED_PARTS: Array<{ sub: string; label: string }> = [
    { sub: 'line1', label: 'street address' },
    { sub: 'city', label: 'city' },
    { sub: 'state', label: 'state' },
    { sub: 'postalCode', label: 'postal code' }
];

/** "a", "a and b", "a, b and c" */
function joinReadable(items: string[]): string {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * An address answer is an object of parts, so it can't be validated as a string
 * — that's what produced Yup's raw "must be a `string` type" message. Validated
 * as one unit rather than as a shape, so the error stays a single sentence the
 * field can render (a shape would put an object in formik's error slot).
 */
function addressSchema(label: string, required: boolean): Yup.AnySchema {
    return Yup.mixed().test({
        name: 'address',
        test(value) {
            if (!required) return true;
            const parts = (value ?? {}) as Record<string, unknown>;
            const filled = (sub: string) => String(parts[sub] ?? '').trim().length > 0;
            const missing = ADDRESS_REQUIRED_PARTS.filter((p) => !filled(p.sub));
            if (missing.length === 0) return true;
            // Nothing entered at all reads better as a plain "required".
            const message = missing.length === ADDRESS_REQUIRED_PARTS.length ? `${label} is required` : `${label}: please add the ${joinReadable(missing.map((p) => p.label))}`;
            return this.createError({ message });
        }
    });
}

function buildInitialValues(pages: RenderPage[]): Values {
    const values: Values = {};
    const add = (b: Block) => {
        if (!isInput(b)) return;
        const key = fieldKey(b);
        if (b.type === 'multiselect' || b.type === 'checkbox' || b.type === 'file') values[key] = [];
        else if (b.type === 'consent') values[key] = false;
        else if (b.type === 'address') values[key] = {};
        else if (b.type === 'yesno') {
            values[key] = '';
            values[`${key}_text`] = '';
        } else values[key] = '';
    };
    pages.forEach((page) => page.blocks.forEach(add));
    return values;
}

/**
 * Yup shape for the answers that are actually in play: the pages on the branch
 * these values route through, and within them only the blocks whose visibility
 * condition passes. Rebuilt per validation (via Yup.lazy) because both of those
 * change as the visitor answers. The server enforces the same subset.
 */
function buildSchema(pages: RenderPage[], values: Values) {
    const shape: Record<string, Yup.AnySchema> = {};
    const add = (b: Block) => {
        if (!isInput(b)) return;
        const key = fieldKey(b);
        const label = (b.props.label as string) || key;
        const required = Boolean(b.props.required);
        if (b.type === 'yesno') {
            shape[key] = required ? Yup.string().required(`${label} is required`) : Yup.string();
            const showTextOn = (b.props.showTextOn as string) || 'never';
            if (showTextOn !== 'never') {
                const textKey = `${key}_text`;
                let textSchema: Yup.AnySchema = Yup.string();
                if (b.props.textRequired) {
                    textSchema = Yup.string().when(key, {
                        is: (v: string) => showTextOn === 'both' || v === showTextOn,
                        then: (schema) => schema.required(`${label} — details are required`),
                        otherwise: (schema) => schema
                    });
                }
                shape[textKey] = textSchema;
            }
            return;
        }
        let s: Yup.AnySchema;
        if (b.type === 'consent') s = Yup.boolean().oneOf([true], `${label} is required`);
        else if (b.type === 'address') s = addressSchema(label, required);
        else if (b.type === 'file') {
            const min = Number(b.props.min) || 0;
            const need = required ? Math.max(1, min) : min;
            s = need > 0 ? Yup.array().min(need, `${label} needs at least ${need} file${need === 1 ? '' : 's'}`) : Yup.array();
        } else if (b.type === 'multiselect' || b.type === 'checkbox') s = required ? Yup.array().min(1, `${label} is required`) : Yup.array();
        else if (b.type === 'number' || b.type === 'currency') s = required ? Yup.number().typeError(`${label} must be a number`).required(`${label} is required`) : Yup.number().typeError(`${label} must be a number`);
        else if (b.type === 'email') s = required ? Yup.string().email(`${label} is invalid`).required(`${label} is required`) : Yup.string().email(`${label} is invalid`);
        else s = required ? Yup.string().required(`${label} is required`) : Yup.string();
        // Short-text validation format (email/phone/ssn/text-only/numbers-only).
        const fmt = b.type === 'text' && b.props.format ? TEXT_FORMATS[b.props.format as string] : null;
        if (fmt) s = (s as Yup.StringSchema).matches(new RegExp(fmt.pattern), { message: fmt.message, excludeEmptyString: true });
        shape[key] = s;
    };
    pages.forEach((page) => visibleBlocks(page.blocks, values).forEach(add));
    return Yup.object().shape(shape);
}

const variants = {
    enter: (dir: number) => ({ x: dir > 0 ? 48 : -48, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -48 : 48, opacity: 0 })
};

const countsAsStep = (p: RenderPage) => p.countsAsStep !== false;

/** Clearance kept below the fixed 80px site header when scrolling a step into view. */
const HEADER_CLEARANCE = 96;

/**
 * Bring an element to rest just under the fixed site header. Shared by the
 * step change and by a failed Next so both move the page the same way.
 */
function scrollBelowHeader(el: HTMLElement | null | undefined) {
    if (!el) return;
    const { top } = el.getBoundingClientRect();
    window.scrollTo({ top: window.scrollY + top - HEADER_CLEARANCE, behavior: 'smooth' });
}

/**
 * A page with no blocks at all is a routing junction — something the author
 * drew to join branches, not a step to show — so the wizard passes through it.
 *
 * Deliberately based on what was authored, NOT on what's currently visible: if
 * a page's blocks are all hidden by their conditions it's still a real step, and
 * skipping it would let a block-level condition silently reroute the form (and,
 * on a terminal page, end it early).
 */
const hasBlocks = (page: RenderPage | undefined) => (page?.blocks?.length ?? 0) > 0;

/**
 * The next page after `fromId`: follow the branch these answers take, passing
 * through junction pages. Returns null at the end of the flow. The `seen` guard
 * keeps a looping graph from spinning.
 */
function nextPageId(byId: Map<string, RenderPage>, fromId: string, values: Values): string | null {
    const seen = new Set<string>([fromId]);
    let id = resolveNextPageId(byId.get(fromId), values);
    while (id && !seen.has(id)) {
        seen.add(id);
        const page = byId.get(id);
        if (!page) return null;
        if (hasBlocks(page)) return id;
        id = resolveNextPageId(page, values);
    }
    return null;
}

/** Entry page: the marked start, advanced past it if it's only a junction. */
function firstPageId(pages: RenderPage[], byId: Map<string, RenderPage>, startId: string | undefined, values: Values): string {
    const start = (startId ? byId.get(startId) : undefined) ?? pages[0];
    if (hasBlocks(start)) return start.id;
    return nextPageId(byId, start.id, values) ?? start.id;
}

/** Steps shown in "Step X of N" — junctions and opted-out pages don't count. */
const countSteps = (list: RenderPage[]) => list.filter((p) => countsAsStep(p) && hasBlocks(p)).length;

/**
 * What gets posted: only the answers the visitor actually saw. Anything hidden
 * by a condition, or sitting on a branch they didn't take (including a branch
 * they filled in before changing their mind), is left out.
 */
function collectAnswers(pages: RenderPage[], startId: string | undefined, values: Values): Values {
    const entries = resolvePagePath(pages, startId, values)
        .flatMap((page) => visibleBlocks(page.blocks, values))
        .filter(isInput)
        .flatMap((block) => {
            const key = fieldKey(block);
            const pairs: Array<[string, unknown]> = [[key, values[key]]];
            // A Yes/No block's follow-up text only counts when it was revealed.
            if (block.type === 'yesno') {
                const showTextOn = (block.props.showTextOn as string) || 'never';
                if (showTextOn !== 'never' && (showTextOn === 'both' || values[key] === showTextOn)) pairs.push([`${key}_text`, values[`${key}_text`]]);
            }
            return pairs;
        });
    return Object.fromEntries(entries) as Values;
}

/**
 * The same answers, labelled and stripped of empties, for a Review block. Built
 * from the path taken so a branch the visitor backed out of isn't summarised
 * back to them.
 */
function summarizeAnswers(pages: RenderPage[], startId: string | undefined, values: Values): AnswerSummary[] {
    return resolvePagePath(pages, startId, values)
        .flatMap((page) => visibleBlocks(page.blocks, values))
        .filter(isInput)
        .map((block) => ({ key: fieldKey(block), label: (block.props.label as string) || fieldKey(block), value: values[fieldKey(block)] }))
        .filter(({ value }) => value !== '' && value != null && !(Array.isArray(value) && value.length === 0));
}

function WizardInner({ pages, startId, maxWidth }: { pages: RenderPage[]; startId?: string; maxWidth: number }) {
    const { values, validateForm, setTouched, touched, submitForm, isSubmitting } = useFormikContext<Values>();
    const auth = useAuth();
    const user = auth?.user;
    const byId = useMemo(() => new Map(pages.map((p) => [p.id, p])), [pages]);
    // Which pages come next isn't fixed — it depends on the answers — so the
    // wizard tracks the trail it has actually walked instead of an index. The
    // last entry is the current page; Back pops it.
    const [trail, setTrail] = useState<string[]>(() => [firstPageId(pages, byId, startId, {})]);
    const [dir, setDir] = useState(1);
    const currentId = trail[trail.length - 1];
    const page = byId.get(currentId) ?? pages[0];

    // Blocks hidden by a condition aren't rendered, don't gate the step, and
    // aren't submitted.
    const blocks = useMemo(() => visibleBlocks(page.blocks, values), [page, values]);
    // The end of the flow is wherever no further page matches — not a fixed
    // last index, since each branch can end somewhere different.
    const nextId = useMemo(() => nextPageId(byId, page.id, values), [byId, page, values]);
    const isLast = nextId === null;

    // A Yes/No block owns two data keys (choice + follow-up text), so the
    // step's error gate must consider both.
    const stepKeys = useMemo(
        () => blocks.filter(isInput).flatMap((b) => (b.type === 'yesno' ? [fieldKey(b), `${fieldKey(b)}_text`] : [fieldKey(b)])),
        [blocks]
    );
    // A step is a sign-in gate if it holds an Auth block; can't advance past it
    // until the visitor is authenticated. Everything after is thus protected.
    const isGate = useMemo(() => blocks.some((b) => b.type === 'auth'), [blocks]);
    const canProceed = !(isGate && !user);

    // Swapping steps takes the outgoing one out of the layout before the incoming
    // one is mounted, so without this the container collapses to nothing for a
    // moment: the document shrinks, the browser clamps the scroll offset, and the
    // visitor loses their place. Holding the outgoing step's height keeps the
    // page still until the new step is laid out (released in onExitComplete,
    // which fires in the same commit that mounts it).
    const stepBoxRef = useRef<HTMLDivElement | null>(null);
    const [heldHeight, setHeldHeight] = useState<number>();

    // Each input block's wrapper, so a blocked Next can bring the offending
    // field into view. A ref map rather than a `data-` attribute lookup because
    // field keys are authored in the CMS and would need escaping in a selector.
    const fieldRefs = useRef(new Map<string, HTMLElement>());
    // A Yes/No block owns two keys but renders one wrapper, so both point at it.
    const registerField = (keys: string[]) => (el: HTMLElement | null) => {
        keys.forEach((k) => {
            if (el) fieldRefs.current.set(k, el);
            else fieldRefs.current.delete(k);
        });
    };
    const holdHeight = useCallback(() => {
        const height = stepBoxRef.current?.offsetHeight;
        if (height) setHeldHeight(height);
    }, []);

    const goNext = useCallback(async () => {
        if (isGate && !user) return; // gate: must sign in first
        const errors = await validateForm();
        const stepErrors = stepKeys.filter((k) => (errors as Record<string, unknown>)[k]);
        if (stepErrors.length > 0) {
            setTouched({ ...(touched as object), ...Object.fromEntries(stepKeys.map((k) => [k, true])) } as never);
            // Nothing visibly happens on a long step otherwise: the messages
            // appear above the fold while the visitor is still at the Next
            // button. `stepKeys` is in block order, so the first failing key is
            // the topmost field. Measured a frame later because the messages
            // mount with this same update and push what's below them down.
            requestAnimationFrame(() => scrollBelowHeader(fieldRefs.current.get(stepErrors[0])));
            return;
        }
        if (!nextId) {
            await submitForm();
            return;
        }
        // Re-resolved here rather than trusting `nextId`: an answer given on this
        // very step can change which branch it leads to.
        const to = nextPageId(byId, currentId, values);
        if (!to) return;
        setDir(1);
        holdHeight();
        setTrail((t) => [...t, to]);
    }, [isGate, user, validateForm, stepKeys, touched, setTouched, nextId, submitForm, byId, currentId, values, holdHeight]);

    const goBack = useCallback(() => {
        if (trail.length <= 1) return;
        setDir(-1);
        holdHeight();
        setTrail((t) => t.slice(0, -1));
    }, [trail.length, holdHeight]);

    // "Step X of N" respects `countsAsStep`: uncounted pages don't inflate the
    // total or the current position, and the counter only shows when the page
    // opted in via `showStepCount`. With branching the total is a projection —
    // the path these answers currently imply — so it can move as they change.
    const currentNumber = useMemo(() => countSteps(trail.map((id) => byId.get(id)).filter(Boolean) as RenderPage[]), [trail, byId]);
    const totalCounted = useMemo(() => Math.max(countSteps(resolvePagePath(pages, startId, values)), currentNumber), [pages, startId, values, currentNumber]);
    const showCount = page.showStepCount === true && countsAsStep(page);
    const canGoBack = trail.length > 1;

    const answers = useMemo(() => summarizeAnswers(pages, startId, values), [pages, startId, values]);
    const nav = useMemo<WizardNav>(
        () => ({ goNext, goBack, isFirst: trail.length === 1, isLast, isSubmitting, canProceed, answers }),
        [goNext, goBack, trail.length, isLast, isSubmitting, canProceed, answers]
    );

    // A signed-in visitor has no reason to see the sign-in gate — skip it
    // entirely (on landing, on sign-in completing, and for already-signed-in
    // users), moving in the current direction so Back still works.
    const goNextRef = useRef(goNext);
    goNextRef.current = goNext;
    const goBackRef = useRef(goBack);
    goBackRef.current = goBack;
    useEffect(() => {
        if (!isGate || !user) return;
        if (dir < 0 && trail.length > 1) goBackRef.current();
        else goNextRef.current();
        // Re-evaluate only when the landed step or auth state changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentId, isGate, user]);

    // Every step starts at the top of the form: steps differ in length, so
    // carrying the previous scroll offset over drops the visitor into the middle
    // of the new step, or below it entirely. Skipped on landing — arriving on the
    // form isn't a step change, and the page shouldn't move on its own.
    const shellRef = useRef<HTMLDivElement | null>(null);
    const landed = useRef(false);
    useEffect(() => {
        if (!landed.current) {
            landed.current = true;
            return;
        }
        scrollBelowHeader(shellRef.current);
    }, [currentId]);

    return (
        <WizardNavContext.Provider value={nav}>
            <div ref={shellRef} className="mx-auto w-full" style={{ maxWidth }}>
                {/* Top chrome: Back (only when there are prior steps) then the optional
                    step counter, each on its own line, left-aligned. */}
                <div className="mb-6 flex min-h-[24px] flex-col items-start gap-10">
                    {canGoBack ? (
                        <button type="button" onClick={goBack} disabled={isSubmitting} className="text-body-400 flex items-center gap-[8px] text-ink-300 transition-colors duration-press ease-ds-out hover:text-ink-400 disabled:opacity-40">
                            <DSIcon name="arrow-left" size={20} /> Back
                        </button>
                    ) : null}
                    {showCount ? (
                        <span className="text-body-300 text-ink-300">
                            Step {currentNumber} of {totalCounted}
                        </span>
                    ) : null}
                </div>

                {/* Sliding step; advancing is driven by a Next block inside the step.
                    The clip box only exists to mask the horizontal slide, but it sat
                    flush against the fields: they run the full width of the shell, so
                    its edges and theirs were the same line. Anything a browser paints
                    OUTSIDE a focused field's own box (the focus ring, and Safari's
                    autofill highlight) was therefore shaved off down both sides, which
                    is what made a focused input look cut. Inset the clip box by 8px
                    all round and pull it back out with an equal negative margin: the
                    step lands where it always did, with room for the ring. The 8px of
                    slide that now escapes is far inside the page's 16px gutter, so it
                    still can't push the document sideways. */}
                <div ref={stepBoxRef} className="relative -m-2 overflow-hidden p-2" style={{ minHeight: heldHeight }}>
                    <AnimatePresence mode="wait" custom={dir} onExitComplete={() => setHeldHeight(undefined)}>
                        <motion.div key={page.id} custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: 'easeInOut' }}>
                            {/* 32px between each component; spacing inside a component is its own. */}
                            <div className="flex flex-col gap-8">
                                {blocks.map((b) => (
                                    <div key={b.id} ref={isInput(b) ? registerField(b.type === 'yesno' ? [fieldKey(b), `${fieldKey(b)}_text`] : [fieldKey(b)]) : undefined}>
                                        <BlockView block={b} />
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </WizardNavContext.Provider>
    );
}

/**
 * Single-page multi-step wizard. All steps live in one component (no route
 * changes); advancing/going back slides horizontally via framer-motion.
 * formik holds answers; each step validates its own fields before advancing;
 * the final step posts to the Next.js submit proxy.
 */
export function FormWizard({ schema, slug }: { schema: FormRenderSchema; slug: string }) {
    // Every page is kept, empty ones included: an empty page can still be a
    // junction whose branches lead somewhere, so the wizard skips over it at
    // navigation time rather than dropping it from the graph.
    const pages = useMemo(() => schema.pages ?? [], [schema.pages]);
    const { startId } = schema;
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const initialValues = useMemo(() => buildInitialValues(pages), [pages]);
    // Lazy so each validation pass sees the answers, and can scope itself to the
    // branch they route through and the blocks they leave visible.
    const validationSchema = useMemo(() => Yup.lazy((values) => buildSchema(resolvePagePath(pages, startId, (values ?? {}) as Values), (values ?? {}) as Values)), [pages, startId]);
    // Form-level max width (px), configurable in the builder. Default 480.
    const maxWidth = Number(schema.settings?.maxWidth) || 480;

    if (pages.length === 0) return null;

    if (submitted) {
        return (
            <div className="mx-auto w-full px-4 text-center" style={{ maxWidth }}>
                <h2 className="text-h1 mb-[8px] text-ink-400">Thank you</h2>
                <p className="text-body-200 text-ink-300">Your submission has been received.</p>
            </div>
        );
    }

    return (
        // pt clears the fixed 80px site header so the first step isn't hidden behind it.
        <div className="w-full px-4">
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (values, helpers) => {
                    setSubmitError(null);
                    try {
                        const res = await fetch(`/api/forms/${encodeURIComponent(slug)}/submit`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            // Only the answers on the path taken — see collectAnswers.
                            body: JSON.stringify({ data: collectAnswers(pages, startId, values), finalize: true })
                        });
                        if (res.status === 401) {
                            setSubmitError('Please sign in to submit this form.');
                            return;
                        }
                        if (!res.ok) {
                            const body = await res.json().catch(() => ({}));
                            setSubmitError(body?.error || 'Something went wrong. Please try again.');
                            return;
                        }
                        setSubmitted(true);
                    } catch {
                        setSubmitError('Network error. Please try again.');
                    } finally {
                        helpers.setSubmitting(false);
                    }
                }}
            >
                <>
                    <WizardInner pages={pages} startId={startId} maxWidth={maxWidth} />
                    {submitError ? <p className="text-body-400 mx-auto mt-[16px] max-w-2xl text-center text-accent-red">{submitError}</p> : null}
                </>
            </Formik>
        </div>
    );
}

export default FormWizard;
